// common types

export type FormData = {
  pluginName: string;
  pluginLogo?: string;
  version: string;
  tags: string[];
  description1: string;
  description2: string;
  ls_version: {
    minimum: string;
  };
  id: string;
  interpreter: {
    type: string;
    version: string;
  };
  type: string;
  author: string;
  lsUser?: string;
  createdDate: string;
  updatedDate: string;
  productType: string;
  ls_product_type: string[];
  pluginDependency: {
    name: string;
    id: string;
  }[];
  is_edit?: boolean;
  status?: number;
};

export type UIField = {
  fieldId: number;
  fieldTitle: string;
  givenName?: string;
  fieldType?: string;
  field_subtype?: string;
  placeholder?: string;
  default?: string;
  infoIcon?: string;
  dynamicRequest?: string;
  values?: {
    displayName: string | number;
    value: string | number;
    default?: string;
    isDynamic?: number;
    dynamiDataSource?: { type: string }[];
  }[];
  onloadAction?: {
    requestName: string;
  };
  userAction?: {
    requestName: string;
    trigger?: string;
    action?: string;
  };
  fromVariable?: boolean;
  sourceBundleId?: string | number;
  selectedVariable?: string;
  labelKey?: string;
  valueKey?: string;
  uid?: number;
};

export type UIForm = {
  formTitle?: string;
  userAction?: {
    action: string;
    requestName?: string;
  };
  fieldList: UIField[];
  uid?: number;
};

export type UIPageType = {
  uid?: number;
  pageTitle: string;
  pageType?: string;
  infoIcon?: string;
  helpContent?: string;
  description?: string;
  forms?: UIForm[];
  uiPages?: UIPageType[];
  content?: string;
};

export type RequestChain = {
  requestId: number;
  url: string;
  method: string;
  params: any[];
  headers: any[];
  auth?: {
    type: string;
    token: string;
  };
  body: any;
  isLoop: boolean;
  loopVariable?: string | null;
  loopSourcePath?: string | null;
  checkpoint: {
    enabled?: boolean;
    initialValue?: string | null;
    key?: string | null;
    format?: string | null;
    paramTemplate?: string | null;
  };
  chainedFrom?: {
    sourceRequestId: number;
    sourcePath?: string | null;
    variableName?: string | null;
  } | null;
};

export type PluginLogicRequest = {
  requestName: string;
  requestType: string;
  scheduled: boolean;
  scheduledFrequency: number;
  lsLogUid?: number;
  responseCategory?: string;
  responseType?: string;
  responsePath?: string;
  responseMapping?: {
    logMapping?: {
      logCategory: string;
      responseIsList: number;
      fields: {
        fieldId: number;
        rawFieldName: string;
        mappedLsField?: string;
        displayName?: string;
        type?: string;
      }[];
    };
    dataListMapping?: {
      logCategory: string;
      fields: {
        fieldId: number;
        rawFieldName: string;
        mappedLsField?: string;
        displayName?: string;
        type?: string;
      }[];
    };
  };
  requestChain: RequestChain[];
};

export type PluginLogic = {
  requests: PluginLogicRequest[];
};

export type FullPluginData = {
  info: FormData;
  uiPages: UIPageType[];
  logic: PluginLogic;
};

export interface pluginInfo {
  client_id: string;
  client_secret: string;
  grant_type: string;
  scope: string;
  tenent_id: string;
  title?: string;
  uid?: number | undefined;
}

export type DefinedVariable = {
  name: string;
  type: "list" | "single";
  scope: "local" | "global";
  sourceRequestId?: number | undefined;
  sourcePath?: string | null | undefined;
  value?: string | undefined;
  isLoop?: boolean | undefined;
  isCommon?: boolean | undefined;
  logType?: string | null | undefined;
  sourceBundleId?: number | null | undefined;
  logicType?: string | null | undefined;
  variableType?: string | null | undefined;
  id?: number | undefined;
  isAuthentication?: boolean | undefined;
  sourceType?: string;
};

export type StdFieldTypes = {
  standard: string;
  id: string;
  [key: string]: any;
  label: string;
  value: string;
}[];

export type RequestBlock = {
  id: number;
  response?: any | null;
  requestId?: number;
};

export type DropdownOption = {
  label: string;
  value: string;
  apiEndpoint?: string;
  target?: string;
  resValues?: { label: string; value: string }[];
  fromVariable?: boolean;
  fromAnomaly?: boolean;
  isApiValue?: boolean;
  isAnomalies?: boolean;
  selectedVariable?: any;
};

export type FieldType =
  | "select"
  | "input"
  | "dropdown"
  | "import_settings"
  | "submit";

export type FieldItem = {
  fieldName: string;
  fieldType: FieldType;
  defaultValue?: string;
  dropdownOptions?: {
    label: string;
    value: string;
    apiEndpoint?: string;
    resValues?: { label: string; value: string }[];
  }[];
  dependsOnField?: string;
  fromVariable?: boolean;
  fromAnomaly?: boolean;
  isApiValue?: boolean;
  dropdownTree?: DropdownOption[];
  selectedVariable?: any;
  uid?: string;
  userAction?: {
    action: string;
    requestName?: string;
    trigger?: string;
    bundle?: string;
    bundleVariable?: string;
    mapResponse?: {
      label: string;
      value: string;
    };
    targetValue?: string;
    targetField?: string;
  };
  dynamicRequest?: string;
  configDropdownTree?: any[][];
  submitConfig?: {
    Ismultiple: boolean;
    bundleId: string;
    requestId: string;
    submit_types: Array<{
      field_given_name: string;
      option_given_name: string;
      bundleId: string;
      requestId: string;
    }>;
  };
  isConfig?: boolean;
  bundleId?: string;
  requestId?: string;
  placeholder?: string;
  infoIcon?: string;
  onloadAction?: {
    requestName?: string;
  };
};

export interface TargetField {
  fieldName: string;
  sourceType: "bundle" | "default";
  bundleId?: string;
  variableName?: string;
  labelKey?: string;
  valueKey?: string;
  availableKeys?: string[];
  defaultOptions?: { label: string; value: string }[];
}

export type DropdownNode = {
  label: string;
  value: string;
  target?: string;
  children?: DropdownNode[]; // nested dependent options
  bundleId?: string;
  variableName?: string;
  labelKey?: string;
  valueKey?: string;
  availableKeys?: string[];
  resValues?: { label: string; value: string }[];
  options?: { label: string; value: string }[];
  isDefaultOption?: boolean;
  targetFields?: TargetField[];
  applicableOptions?: any[];
};

export type BlockRoot = {
  root: DropdownNode; // the “block’s input” node
};

export type LogicType = {
  name: string;
  data?: any[];
};

export type LogicTypeWithState = LogicType & {
  blocks?: RequestBlock[];
};

export interface Variable {
  id: number;
  name: string;
  scope: "global" | "bundle";
  type: string;
  sourceBundleId: number;
  sourceRequestId: number;
  sourcePath: string;
  isCommon: boolean;
}

export interface MappingField {
  fieldId: number;
  rawFieldName: string;
  type: string;
  mappingLsField: string;
  display_name: string;
  category: string;
}

export interface Mapping {
  logUid: number;
  logTitile: string;
  logType: string;
  logCategory: string;
  log_ls_product_type: string[];
  logCronFrequency: number;
  mappingFields: MappingField[];
}

export interface Request {
  requestId: number;
  url: string;
  method: string;
  params: string[];
  headers: { key: string; value: string }[];
  auth: {
    type: string;
    token: string;
  };
  body: string;
  isLoop: boolean;
  loopOnVariable: string;
  checkpoint: {
    produces: {
      enabled: boolean;
      sourcePath: string;
    };
    consumes: {
      enabled: boolean;
      paramTemplate: string;
    };
  };
  flag: number;
  plugin_id: string;
  ssl_verify: boolean;
  configuration_Id: string;
  mappingId: string;
}

export interface LogicBundle {
  bundleId: number;
  bundleTitle: string;
  bundleDescription: string;
  bundleType: string;
  bundleConfigVariables: {
    name: string;
    sourcePath: string;
  }[];
  mappings: Mapping[];
  requests: Request[];
}

export interface LogicPagePayload {
  variables: Variable[];
  LogicBundles: LogicBundle[];
  is_edit: boolean;
}

export type Bundle = {
  bundleTitle: string;
  bundleDescription?: string;
  bundleId?: number;
  requests?: any[];
  mappings?: any[];
};

export type VariableItem = [
  { name: "Action"; data: [] },
  { name: "Save Logs"; data: [] },
  { name: "Settings"; data: [] },
];

//  initial data

export const INFO_DATA: FormData = {
  pluginName: "",
  pluginLogo: "",
  version: "",
  tags: [],
  description1: "",
  description2: "",
  ls_version: {
    minimum: "",
  },
  id: "",
  interpreter: {
    type: "python",
    version: "3",
  },
  type: "",
  author: "",
  createdDate: "",
  updatedDate: "",
  productType: "",
  ls_product_type: [],
  pluginDependency: [],
};

export const UI_DATA: UIPageType[] = [
  {
    pageTitle: "",
    pageType: "",
    infoIcon: "",
    description: "",
    helpContent: "",
    // forms: [
    //     {
    //         formTitle: "",
    //         userAction: {
    //             action: "",
    //             requestName: "",
    //         }
    //     },
    // ]
  },
];

// Function to update fields
export const LS_PRODUCT_TYPE = [
  {
    label: "NDR",
    value: "ndr",
  },
  {
    label: "DSPM",
    value: "ddr",
  },
  {
    label: "ITDR",
    value: "itdr",
  },
  { label: "CMX", value: "cmx" },
];

export const uiTypes = [
  {
    label: "Settings",
  },
  {
    label: "Action",
  },
];

export const fieldMappingFunctions = [
  {
    label: "Convert to Uppercase",
    value: "convert_to_uppercase",
    isInput: false,
  },
  {
    label: "Capitalize Each Word",
    value: "capitalize_each_word",
    isInput: false,
  },
  {
    label: "Remove Digits",
    value: "remove_digits",
    isInput: false,
  },
  {
    label: "Extract Digits",
    value: "extract_digits",
    isInput: false,
  },
  {
    label: "Extract String From Left",
    value: "extract_string_from_left",
    isInput: true,
  },
  {
    label: "Remove Punctuations",
    value: "remove_punctuations",
    isInput: false,
  },
  {
    label: "Extract Email",
    value: "extract_email",
    isInput: false,
  },
  {
    label: "Remove Newlines",
    value: "remove_newlines",
    isInput: false,
  },
  {
    label: "Remove Escape Character",
    value: "remove_escape_character",
    isInput: false,
  },
  {
    label: "Convert To Lowercase",
    value: "convert_tolowercase",
    isInput: false,
  },
  {
    label: "Remove Spaces",
    value: "remove_spaces",
    isInput: false,
  },
  {
    label: "Extract String From Right",
    value: "extract_string_fromright",
    isInput: true,
  },
  {
    label: "Extract IP Address",
    value: "extract_ipaddress",
    isInput: false,
  },
  {
    label: "Extract Country",
    value: "extract_country",
    isInput: false,
  },
  {
    label: "Extracting File Name",
    value: "{{access}}",
    isInput: false,
  },
  {
    label: "Extract Domain",
    value: "extract_tld",
    isInput: false,
  },
  {
    label: "Extract Parent URI",
    value: "extract_parent_uri",
    isInput: false,
  },
  {
    label: "Extracting File Extension",
    value: "extracting_file_extention",
    isInput: false,
  },
  {
    label: "Get Geolocation From IP",
    value: "get_geolocation_from_ip",
    isInput: false,
  },
];

export const fieldTypes = [
  "number",
  "string",
  "domain",
  "ip",
  "email",
  "url",
  "filename",
  "integer",
  "hash",
  "time",
  "mac",
  "username",
  "port",
];

export const anomalyFieldTypes = [
  { label: "Source IP", value: "source_ip" },
  { label: "Destination IP", value: "destination_ip" },
  { label: "Source Port", value: "source_port" },
  { label: "Destination Port", value: "destination_port" },
  { label: "Protocol", value: "protocol" },
  { label: "Anomaly Type", value: "anomaly_type" },
  { label: "Anomaly ID", value: "anomaly_id" },
  { label: "Anomaly Name", value: "anomaly_name" },
  { label: "username", value: "username" },
];

export const settingsFieldTypes = [
  { label: "Checkbox", value: "checkbox" },
  { label: "Region and Country", value: "region_country" },
  { label: "Domain", value: "domain" },
  { label: "Dropdown", value: "dropdown" },
  { label: "Host Address", value: "host_address" },
  { label: "Input – Date", value: "input_date" },
  { label: "Input – Number", value: "input_number" },
  { label: "Input – Password", value: "input_password" },
  { label: "Input – Text", value: "input_text" },
  { label: "Input – Textarea", value: "input_textarea" },
  { label: "IP", value: "ip" },
  { label: "MAC", value: "mac" },
  { label: "Multi-select Dropdown", value: "multi_select_dropdown" },
  { label: "Port", value: "port" },
  { label: "Proxy", value: "proxy" },
  { label: "Radio", value: "radio" },
  { label: "URL", value: "url" },
];

export const RESERVED_SETTINGS_GIVEN_NAMES = settingsFieldTypes.map((field) =>
  String(field.value).toLowerCase(),
);

// Validation patterns
export const VALIDATION_PATTERNS = {
  ip: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
  domain:
    /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.[a-z]{2,}$/i,
  mac: /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/,
  port: /^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/,
  host_address:
    /^(localhost|((25[0-5]|2[0-4]\d|1?\d?\d)\.(25[0-5]|2[0-4]\d|1?\d?\d)\.(25[0-5]|2[0-4]\d|1?\d?\d)\.(25[0-5]|2[0-4]\d|1?\d?\d))|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}))$/,
  url: /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d+)?(\/\S*)?$/,
};

export const logSaveCategories = [
  { label: "User Audit", value: "user_audit" },
  { label: "User Access", value: "user_access" },
  { label: "DHCP", value: "dhcp" },
  { label: "Email", value: "email" },
  { label: "File", value: "file" },
  { label: "Database", value: "database" },
  { label: "Conversations", value: "conversations" },
  { label: "File Access", value: "file_access" },
  { label: "Database Access", value: "database_access" },
  { label: "Email Access", value: "email_access" },
  { label: "DLP", value: "dlp" },
  { label: "Endpoint", value: "endpoint" },
  { label: "NAC", value: "nac" },
  { label: "Firewall", value: "firewall" },
  { label: "Proxy", value: "proxy" },
  { label: "DNS Security", value: "dnssecurity" },
  { label: "Netflow", value: "netflow" },
  { label: "OT Security", value: "otsecurity" },
  { label: "SIEM", value: "siem" },
  { label: "Syslog Forwarder", value: "syslog_forwarder" },
  { label: "VPN", value: "vpn" },
  { label: "Vulnerability Scanner", value: "vulnerability_scanner" },
  { label: "Other", value: "other" },
  { label: "DDOS", value: "ddos" },
  { label: "Router", value: "router" },
];

//pagination options
export const PAGINATIONOPTION = [
  { label: "None", value: "none" },
  { label: "Offset / Limit", value: "offset_limit" },
  { label: "Page & Page Size", value: "page_page_size" },
  { label: "Cursor", value: "cursor" },
  { label: "Token", value: "token" },
  { label: "Next URL", value: "next_url" },
  // { label: "Header", value: "header" },
  // { label: "Time", value: "time" },
];

// date time formats
export const DATE_TIME_FORMAT = [
  { label: "None", value: "none" },

  { label: "Epoch Seconds", example: "1705326330", value: "epoch_seconds" },
  {
    label: "Epoch Milliseconds",
    example: "1705326330123",
    value: "epoch_miliseconds",
  },

  { label: "Local Time", example: "2024-01-15 13:45:30", value: "local_time" },

  {
    label: "Log Format with Milliseconds",
    example: "2024-01-15 13:45:30.123",
    value: "log_format_with_miliseconds",
  },

  { label: "UTC Zulu", example: "2024-01-15T13:45:30Z", value: "utc_zulu" },
  {
    label: "UTC Milliseconds",
    example: "2024-01-15T13:45:30.123Z",
    value: "utc_miliseconds",
  },

  {
    label: "UTC Offset Format",
    example: "2024-01-15T13:45:30+00:00",
    value: "utc_offset_format",
  },

  { label: "Date Only Format", example: "2024-01-15", value: "date_only" },
  { label: "Week Date Format", example: "2024-W03-1", value: "week_date" },
  { label: "Ordinal Date Format", example: "2024-015", value: "ordinal_date" },

  { label: "Custom Format", value: "custom", example: "YYYY/MM/DD hh:mm:ss" },
];

// log collection frequencies
export const LOG_COLLECTION_PERIOD = [
  { label: "Last 1 hour", value: "one_hour" },
  { label: "Last 1 day", value: "one_day" },
  { label: "Last 7 days", value: "seven_days" },
  { label: "Last 30 days", value: "thirty_days" },
];

type FormatToken = {
  id: string;
  label: string;
  value: string;
  description: string;
};

export const FORMAT_TOKENS: FormatToken[] = [
  { id: "YYYY", label: "YYYY", value: "%Y", description: "Year (4 digits)" },
  { id: "YY", label: "YY", value: "%y", description: "Year (2 digits)" },
  { id: "MM", label: "MM", value: "%m", description: "Month " },
  { id: "DD", label: "DD", value: "%d", description: "Day " },
  { id: "HH", label: "HH", value: "%H", description: "Hour 24h " },
  { id: "hh", label: "hh", value: "%I", description: "Hour 12h " },
  { id: "mm", label: "mm", value: "%M", description: "Minute " },
  { id: "ss", label: "ss", value: "%S", description: "Second " },
  // { id: "A", label: "AM/PM", value: "%p", description: "AM or PM" },
];

export const TIMEZONE_TOKENS: FormatToken[] = [
  { id: "UTC", label: "UTC", value: "%Z:UTC", description: "UTC timezone" },
  { id: "GMT", label: "GMT", value: "%Z:GMT", description: "GMT timezone" },
  { id: "EST", label: "EST", value: "%Z:EST", description: "Eastern (US)" },
  { id: "CST", label: "CST", value: "%Z:CST", description: "Central (US)" },
  { id: "MST", label: "MST", value: "%Z:MST", description: "Mountain (US)" },
  { id: "PST", label: "PST", value: "%Z:PST", description: "Pacific (US)" },
  { id: "IST", label: "IST", value: "%Z:IST", description: "Indian Standard" },
  { id: "JST", label: "JST", value: "%Z:JST", description: "Japan Standard" },
  {
    id: "AEST",
    label: "AEST",
    value: "%Z:AEST",
    description: "Australian Eastern",
  },
  { id: "CET", label: "CET", value: "%Z:CET", description: "Central European" },
];

export const SEPARATORS = [
  { id: "dash", label: "-", value: "-" },
  { id: "slash", label: "/", value: "/" },
  { id: "colon", label: ":", value: ":" },
  { id: "space", label: "Space", value: " " },
  { id: "dot", label: ".", value: "." },
  { id: "T", label: "T", value: "T" },
];

export const AUTH_TYPES = [
  { label: "Authorization", value: "authorization" },
  { label: "API-Key", value: "api-key" },
  { label: "Bearer Token", value: "bearer" },
  { label: "JWT Bearer", value: "jwtBearer" },
  { label: "Basic Auth", value: "basic" },
];
